/**
 * The world has an outside, and this is the whole rule about it (design D1).
 *
 * Two questions and an answer. *Has this object left the world* — a height comparison
 * against a kill plane, because the grass collider is exactly the maze plus its apron and
 * the moat's water is drawn but never collidable, so anything that clears the bank falls
 * forever. *Is the player standing on lawn outside the labyrinth* — a point against a
 * handful of boxes `buildGrounds` derives from the maze. And *where do we put them back* —
 * the centre of the nearest cell, which is clear of hedges by construction.
 *
 * A kill-plane collider and an out-of-bounds sensor are the obvious Rapier answer and were
 * rejected: they cost two colliders per level and fire body mutations from inside Rapier's
 * own event dispatch, which is the one place a body must not be moved. A comparison costs
 * nothing, and living here it can be tested with no renderer and no GPU.
 *
 * Pure, allocation-free on the hot path, and no three.js, React Three Fiber or Rapier
 * anywhere near it (architectural rule 1).
 */

import { CELL_SIZE } from './maze/grid';
import type { Box, GroundPoint } from './profile/types';

/**
 * Below this, an object is falling and not coming back on its own. Every floor in the game
 * is at y = 0 and the moat's water surface is at -0.8, so -3 is under anything that can
 * legitimately rest somewhere, and far above the altitude a fall reaches in the frame
 * after it starts.
 */
export const OUT_OF_BOUNDS_Y = -3;

/** How far below the lawn an out-of-bounds box reaches, so a settled capsule is inside it. */
export const OUT_OF_BOUNDS_BOTTOM = -1;

/**
 * How far below a hedge's crest an out-of-bounds box stops (design D3).
 *
 * Walking a crest is the reward the ladders exist for, including on the perimeter hedge
 * whose outer half stands over out-of-bounds lawn — so the boxes have to end below the
 * surface a crest-walker stands on. A hedge collider is its full `hedgeHeight` (the 5%
 * visual inset is not something a capsule can feel), so this is pure clearance against a
 * settling error: comfortably more than one, comfortably less than the drop off the side.
 * Step off the outside and you pass through it long before you land.
 */
export const CREST_MARGIN = 0.25;

/**
 * How high above the lawn a recovered player's feet are put down (design D6).
 *
 * `placeTables` centres a table exactly on a cell centre and `DragController` then lets
 * the player move it, so no cell centre is reliably clear and the built table list is not
 * where the tables are. A metre clears a 0.75 m table top, is far below the 3 m hedge, and
 * falls in about a third of a second — it reads as being set back down. The alternative,
 * hunting for a table-free cell, fails the moment a table is dragged and wedges the player
 * inside one when it does.
 */
export const RECOVERY_FEET_CLEARANCE = 1;

/**
 * The lawn a level's player may not stand on, plus the grid they are put back into. One
 * value, so either half of the rule takes one argument and `Player` takes one prop.
 */
export type OutOfBounds = {
  /** Boxes over the out-of-bounds lawn, each stopping `CREST_MARGIN` below the crest. */
  boxes: readonly Box[];
  columns: number;
  rows: number;
};

/** Has this object fallen past anything it could land on. */
export function hasLeftTheWorld(y: number): boolean {
  return y < OUT_OF_BOUNDS_Y;
}

/**
 * Is this point inside any of the level's out-of-bounds boxes. Inclusive on every face: a
 * settled capsule reports its feet at exactly 0, and a player stepping off a crest passes
 * through the top on some frame.
 *
 * A plain loop, no allocation — it runs once a frame against at most seven boxes.
 */
export function isOutOfBounds(bounds: OutOfBounds, x: number, y: number, z: number): boolean {
  for (const box of bounds.boxes) {
    if (Math.abs(x - box.center.x) > box.size.x / 2) continue;
    if (Math.abs(y - box.center.y) > box.size.y / 2) continue;
    if (Math.abs(z - box.center.z) > box.size.z / 2) continue;
    return true;
  }
  return false;
}

/**
 * The centre of the maze cell nearest this point — where a player out of bounds is put
 * back down.
 *
 * The maze is centred on the origin with a grid line every `CELL_SIZE`, so this is a
 * divide and a clamp per axis. The answer is always half a cell off a grid line, which is
 * the same reasoning `GATE_CLEARANCE` rests on: the middle of a cell is clear of hedges
 * however the maze was generated, so recovery never needs a physics query.
 */
export function nearestCellCentre(bounds: OutOfBounds, x: number, z: number): GroundPoint {
  return {
    x: cellCentreOnAxis(x, bounds.columns),
    z: cellCentreOnAxis(z, bounds.rows),
  };
}

function cellCentreOnAxis(value: number, cells: number): number {
  const edge = (cells * CELL_SIZE) / 2;
  const index = Math.floor((value + edge) / CELL_SIZE);
  const clamped = Math.min(Math.max(index, 0), cells - 1);
  return -edge + clamped * CELL_SIZE + CELL_SIZE / 2;
}
